// Boundary between `QueryFieldFieldsFragment` (BE read shape) and the form
// shape consumed by `FieldSettingsPanel`.
//
// - `toFieldSettingsValues` (read): seeds the form when the panel opens.
// - `applyFieldSettingsToRaw` (write): folds the form values back into the
//   raw fragment so `toQueryFieldInputs` (in `fieldsMapper.ts`) picks them
//   up on the top-level Save.
//
// Both directions reuse the shared data-format enum maps so query-level
// and field-level settings always map enums identically.

import {
  CURRENCY_POSITION_FROM_BE,
  CURRENCY_POSITION_TO_BE,
  NEGATIVE_NUMBERS_FROM_BE,
  NEGATIVE_NUMBERS_TO_BE,
  NULL_DISPLAY_FROM_BE,
  NULL_DISPLAY_TO_BE,
  NUMBER_SCALE_FROM_BE,
  NUMBER_SCALE_TO_BE,
  ZERO_DISPLAY_FROM_BE,
  ZERO_DISPLAY_TO_BE,
} from "../../../../shared/DataFormatSection/enumMaps";
import { dateFormatPatternOrFallback } from "../../../../shared/DataFormatSection/patternBuilder";
import { DEFAULT_DATE_PERIOD_VALUES } from "../../../../shared/DatePeriodSection/constants";
import { normalizeLevelStack } from "../../../../shared/levels/levelDisplay";
import {
  isNoneLevelSelection,
  NONE_LEVEL_VALUE,
  stripNoneLevel,
} from "../../../../shared/levels/noneLevel";
import { supportsTransactionLevel } from "../../../../shared/levels/supportedLevels";
import { findAttributeByPath } from "../../../../shared/RulesSection/attributeCatalogue";
import { toRules, toRuleSetType } from "../../../../shared/RulesSection/mappers";
import { DEFAULT_FIELD_SETTINGS_VALUES } from "./constants";
import { applyDatePeriod } from "./datePeriodRaw";
import type {
  QueryFieldComponentTreeNode,
  QueryFieldComponentTreeNodeWrite,
} from "../fieldsMapper";
import type { FieldSettingsValues, FormulaComponentSettings } from "./types";
import type { DataFormatValues } from "../../../../shared/DataFormatSection/types";
import type { DatePeriodValues } from "../../../../shared/DatePeriodSection/types";
import type { QueryRollupLevelOption } from "../../../../shared/levels/types";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { Rules } from "../../../../shared/RulesSection/types";
import type { FieldDataType, QueryFieldFieldsFragment } from "~/graphql/supergraphTypes";

export const NUMERIC_FIELD_TYPES: ReadonlySet<FieldDataType> = new Set<FieldDataType>([
  "QUANTITY",
  "VALUE",
  "PERCENTAGE",
]);

export const isNumericFieldType = (fieldType: FieldDataType): boolean =>
  NUMERIC_FIELD_TYPES.has(fieldType);

/** NODE fields and node formula-component leaves only support VALUE or QUANTITY. */
export const toNodeFieldType = (fieldType: FieldDataType | null | undefined): FieldDataType =>
  fieldType === "QUANTITY" || fieldType === "VALUE" ? fieldType : "VALUE";

/** Level identifiers a primary field's catalogue column supports, resolved
 * from the attribute leaves (sourced from `finpubPrimaryFields`). Empty for
 * non-primary fields and for columns the catalogue has no mapping data for. */
export const toPrimaryFieldSupportedLevels = (
  raw: QueryFieldFieldsFragment,
  attributeLeaves: ReadonlyArray<AttributeLeaf> = [],
): ReadonlyArray<string> =>
  raw.referenceKind === "PRIMARY_FIELD" && raw.primaryField
    ? (findAttributeByPath(raw.primaryField, attributeLeaves)?.supportedLevels ?? [])
    : [];

/** NODE fields always roll up. Primary fields roll up when numeric, or —
 * regardless of data type — when the column's Semantic Layer mappings
 * support the transaction level (e.g. `transaction.description`, a TEXT
 * column whose values nest under `transactions[]`). */
export const shouldShowLevels = (
  referenceKind: QueryFieldFieldsFragment["referenceKind"],
  fieldType: FieldDataType,
  supportedLevels: ReadonlyArray<string> = [],
): boolean =>
  referenceKind === "NODE" ||
  (referenceKind === "PRIMARY_FIELD" &&
    (isNumericFieldType(fieldType) || supportsTransactionLevel(supportedLevels)));

const UNKNOWN_PRIMARY_FIELD_TYPE: FieldDataType = "TEXT";

const toFieldDataType = (attribute: AttributeLeaf | undefined): FieldDataType => {
  if (!attribute) return UNKNOWN_PRIMARY_FIELD_TYPE;
  if (attribute.dataType === "TIME") return "DATE";
  if (attribute.dataType === "BOOLEAN") return "TEXT";
  // The semantic-layer attribute vocabulary still carries the pre-measure
  // `CURRENCY`/`NUMBER` types; both are dollar-denominated values, so they
  // map onto the `VALUE` measure.
  if (attribute.dataType === "CURRENCY" || attribute.dataType === "NUMBER") return "VALUE";
  return attribute.dataType;
};

/** Date period applies to numeric field types, including primary fields. */
export const shouldShowDatePeriod = (
  raw: QueryFieldFieldsFragment,
  attributeLeaves: ReadonlyArray<AttributeLeaf> = [],
): boolean => isNumericFieldType(toInitialFieldType(raw, attributeLeaves));

// Field-level format columns are nullable on the BE — `null` means "inherit
// query defaults". Each null column resolves to `inheritedFormat` (the
// query-level values from `QuerySettingsPanel`) when the panel passes
// them through; otherwise it falls back to `DEFAULT_FIELD_SETTINGS_VALUES`
// so a brand-new query without explicit query defaults still has a valid
// value for every select.
//
// Threading the inherited values into the seed matters when the user
// flips `overrideFormat` ON and saves without touching any select: the
// form ships whatever it was seeded with, so seeding the hardcoded
// fallbacks would silently overwrite the actual query defaults on the BE.
const FORMAT_DEFAULTS = DEFAULT_FIELD_SETTINGS_VALUES.format;

const toFormatValues = (
  settings: NonNullable<QueryFieldFieldsFragment["settings"]>,
  inheritedFormat: DataFormatValues | undefined,
): DataFormatValues => {
  const fallback = inheritedFormat ?? FORMAT_DEFAULTS;
  return {
    decimalPlaces: settings.decimalPlaces ?? fallback.decimalPlaces,
    negativeNumbers: settings.negativeNumberStyle
      ? NEGATIVE_NUMBERS_FROM_BE[settings.negativeNumberStyle]
      : fallback.negativeNumbers,
    numberScale: settings.numberScale
      ? NUMBER_SCALE_FROM_BE[settings.numberScale]
      : fallback.numberScale,
    thousandsSeparator: settings.thousandsSeparator ?? fallback.thousandsSeparator,
    showCurrencySymbol: settings.showCurrencySymbol ?? fallback.showCurrencySymbol,
    currencySymbolPosition: settings.currencySymbolPosition
      ? CURRENCY_POSITION_FROM_BE[settings.currencySymbolPosition]
      : fallback.currencySymbolPosition,
    zeroDisplay: settings.zeroDisplayStyle
      ? ZERO_DISPLAY_FROM_BE[settings.zeroDisplayStyle]
      : fallback.zeroDisplay,
    nullDisplay: settings.nullDisplayStyle
      ? NULL_DISPLAY_FROM_BE[settings.nullDisplayStyle]
      : fallback.nullDisplay,
    dateFormatPattern: dateFormatPatternOrFallback(
      settings.dateFormatPattern,
      fallback.dateFormatPattern,
    ),
  };
};

/** Converts a raw backend date period into form values. */
export const toDatePeriodValues = (
  datePeriod: QueryFieldFieldsFragment["datePeriod"],
): DatePeriodValues => {
  if (!datePeriod) {
    return { ...DEFAULT_DATE_PERIOD_VALUES };
  }
  return {
    periodKind: datePeriod.periodKind,
    dateType: datePeriod.dateType,
    offsetMonths: datePeriod.offsetMonths,
    trailingMonths: datePeriod.trailingMonths,
    customRangeKind: datePeriod.customRangeKind,
    customStartParameter: datePeriod.customStartParameter,
    customStartDate: datePeriod.customStartDate,
    customEndParameter: datePeriod.customEndParameter,
    customEndDate: datePeriod.customEndDate,
  };
};

/** Flattens a FORMULA field's persisted component tree into the path-keyed
 * form buffer. Anchors (sub-formula entries, i.e. tree entries with
 * children) contribute only their path segment; leaves become entries keyed
 * by the slash-joined chain of catalog `formulaComponentId`s (e.g. `"12/45"`). */
export const toFormulaComponentValues = (
  components: ReadonlyArray<QueryFieldComponentTreeNode>,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption> = [],
): Record<string, FormulaComponentSettings> => {
  const out: Record<string, FormulaComponentSettings> = {};
  const walk = (nodes: ReadonlyArray<QueryFieldComponentTreeNode>, prefix: string) => {
    for (const node of nodes) {
      const pathKey = prefix
        ? `${prefix}/${node.formulaComponentId}`
        : String(node.formulaComponentId);
      const children = node.children ?? [];
      if (children.length > 0) {
        walk(children, pathKey);
      } else {
        out[pathKey] = {
          datePeriod: toDatePeriodValues(node.datePeriod),
          levels: normalizeLevelStack(node.levels?.levels ?? [], rollupLevelOptions),
          fieldType: node.settings?.fieldType ?? null,
          filterRules: toRules(node.settings?.filterRules),
          suppressionRules: toRules(node.settings?.suppressionRules),
        };
      }
    }
  };
  walk(components, "");
  return out;
};

const isSyntheticField = (raw: QueryFieldFieldsFragment): boolean =>
  raw.id < 0 && raw.queryVersionId === 0;

const toInitialFieldType = (
  raw: QueryFieldFieldsFragment,
  attributeLeaves: ReadonlyArray<AttributeLeaf>,
): FieldDataType => {
  if (raw.referenceKind === "NODE") {
    // NODE fields select the measure: VALUE (dollars) or QUANTITY (shares).
    // The node option list carries only those two, so anything else persisted
    // (legacy PERCENTAGE rows) coerces to VALUE — an unmatched select would
    // render blank.
    return toNodeFieldType(raw.settings?.fieldType);
  }
  if (raw.settings) return raw.settings.fieldType;
  if (raw.referenceKind === "DYNAMIC_DATE") return "DATE";
  if (raw.referenceKind !== "PRIMARY_FIELD") return DEFAULT_FIELD_SETTINGS_VALUES.fieldType;
  return raw.primaryField
    ? toFieldDataType(findAttributeByPath(raw.primaryField, attributeLeaves))
    : UNKNOWN_PRIMARY_FIELD_TYPE;
};

const shouldPreselectSingleSyntheticLevel = (
  raw: QueryFieldFieldsFragment,
  supportedLevels: ReadonlyArray<string>,
): boolean =>
  isSyntheticField(raw) &&
  !raw.settings &&
  raw.referenceKind !== "FORMULA_COMPONENT" &&
  shouldShowLevels(raw.referenceKind, DEFAULT_FIELD_SETTINGS_VALUES.fieldType, supportedLevels);

const toLevelValues = (
  raw: QueryFieldFieldsFragment,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
  supportedLevels: ReadonlyArray<string>,
): string[] => {
  const levels = normalizeLevelStack(raw.levels?.levels ?? [], rollupLevelOptions);
  const selected = shouldPreselectSingleSyntheticLevel(raw, supportedLevels)
    ? levels.slice(0, 1)
    : levels;
  // A primary field with no persisted level executes at page level; the
  // form shows that state as an explicit "None" selection rather than an
  // empty stack (which the panel would auto-seed with a real level).
  if (raw.referenceKind === "PRIMARY_FIELD" && selected.length === 0) {
    return [NONE_LEVEL_VALUE];
  }
  return selected;
};

const isEmptyRules = (rules: Rules): boolean => rules.groups.length === 0;

const shouldPreserveUnknownPrimarySettings = (
  raw: QueryFieldFieldsFragment,
  values: FieldSettingsValues,
  attributeLeaves: ReadonlyArray<AttributeLeaf>,
): boolean =>
  raw.referenceKind === "PRIMARY_FIELD" &&
  !raw.settings &&
  !isSyntheticField(raw) &&
  values.fieldType === toInitialFieldType(raw, attributeLeaves) &&
  !values.overrideDecimalPlaces &&
  !values.overrideFormat &&
  isEmptyRules(values.filterRules) &&
  isEmptyRules(values.suppressionRules);

/** Backend `QueryFieldFieldsFragment` → form values for the field panel.
 *
 * `inheritedFormat` should be the query-level `DataFormatValues` from the
 * `QuerySettingsPanel` so nullable BE columns (and the entire format
 * block on a brand-new field with `settings: null`) seed from the actual
 * query defaults instead of hardcoded fallbacks. */
export const toFieldSettingsValues = (
  raw: QueryFieldFieldsFragment,
  inheritedFormat?: DataFormatValues,
  attributeLeaves: ReadonlyArray<AttributeLeaf> = [],
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption> = [],
): FieldSettingsValues => {
  const settings = raw.settings;
  const formatFallback = inheritedFormat ?? DEFAULT_FIELD_SETTINGS_VALUES.format;
  const fieldType = toInitialFieldType(raw, attributeLeaves);
  const isFormulaNumeric = raw.referenceKind === "FORMULA" && isNumericFieldType(fieldType);
  const supportedLevels = toPrimaryFieldSupportedLevels(raw, attributeLeaves);

  return {
    fieldType,
    overrideDecimalPlaces: Boolean(settings?.overrideDecimalPlaces),
    overrideFormat: Boolean(settings?.overrideDisplayFormat),
    format: settings ? toFormatValues(settings, inheritedFormat) : formatFallback,
    datePeriod:
      isNumericFieldType(fieldType) && !isFormulaNumeric
        ? toDatePeriodValues(raw.datePeriod)
        : null,
    levels: shouldShowLevels(raw.referenceKind, fieldType, supportedLevels)
      ? toLevelValues(raw, rollupLevelOptions, supportedLevels)
      : [],
    formulaComponents: isFormulaNumeric
      ? toFormulaComponentValues(raw.components, rollupLevelOptions)
      : {},
    // Seeded from persisted rows, not a live expansion — the panel flips
    // this to true once `useFormulaComponentItems` resolves the leaf set.
    formulaComponentsResolved: false,
    filterRules: toRules(settings?.filterRules),
    suppressionRules: toRules(settings?.suppressionRules),
  };
};

// -- Write side --------------------------------------------------------------

type RawSettings = NonNullable<QueryFieldFieldsFragment["settings"]>;

/** Picker-synthesized fields land with `settings: null` (see
 * `AddFieldPickerSlideout/pickerMapper.ts`). When the user opens the
 * settings panel and edits anything other than `levels` / `alias`, the
 * write path needs a base `RawSettings` to spread the form values onto —
 * otherwise the early-`null` bail dropped fieldType / format / rules
 * silently. The fields here mirror the BE-nullable columns; `fieldType`
 * is always overwritten from the form. */
export const BLANK_RAW_SETTINGS: RawSettings = {
  __typename: "FieldSettingsType",
  fieldType: "QUANTITY",
  overrideDecimalPlaces: false,
  decimalPlaces: null,
  overrideDisplayFormat: false,
  negativeNumberStyle: null,
  numberScale: null,
  thousandsSeparator: null,
  showCurrencySymbol: null,
  currencySymbolPosition: null,
  zeroDisplayStyle: null,
  nullDisplayStyle: null,
  dateFormatPattern: null,
  filterRules: { __typename: "RuleSetType", groupsOperator: "AND", groups: [] },
  suppressionRules: { __typename: "RuleSetType", groupsOperator: "AND", groups: [] },
};

/** Each non-decimal format column is `null` on the BE when the field
 * inherits the query default (see `toFormatValues` above — `null` ⇒
 * inherit). Persisting concrete values when `overrideFormat` is off
 * would pin the field to today's defaults and silently stop it from
 * following future query-level changes; `decimalPlaces` is gated by its
 * own `overrideDecimalPlaces` toggle. */
const applyFormatToSettings = (
  current: RawSettings,
  fieldType: FieldDataType,
  format: DataFormatValues,
  overrideFormat: boolean,
  overrideDecimalPlaces: boolean,
  filterRules: Rules,
  suppressionRules: Rules,
): RawSettings => {
  const isNumeric = NUMERIC_FIELD_TYPES.has(fieldType);
  const supportsNumberScale = fieldType === "QUANTITY" || fieldType === "VALUE";
  const isCurrency = fieldType === "VALUE";
  const isDate = fieldType === "DATE";

  return {
    ...current,
    overrideDecimalPlaces: isNumeric && overrideDecimalPlaces,
    overrideDisplayFormat: overrideFormat,
    decimalPlaces: isNumeric && overrideDecimalPlaces ? format.decimalPlaces : null,
    negativeNumberStyle:
      overrideFormat && isNumeric ? NEGATIVE_NUMBERS_TO_BE[format.negativeNumbers] : null,
    numberScale:
      overrideFormat && supportsNumberScale ? NUMBER_SCALE_TO_BE[format.numberScale] : null,
    thousandsSeparator: overrideFormat && isNumeric ? format.thousandsSeparator : null,
    showCurrencySymbol: overrideFormat && isCurrency ? format.showCurrencySymbol : null,
    currencySymbolPosition:
      overrideFormat && isCurrency
        ? CURRENCY_POSITION_TO_BE[format.currencySymbolPosition]
        : null,
    zeroDisplayStyle:
      overrideFormat && isNumeric ? ZERO_DISPLAY_TO_BE[format.zeroDisplay] : null,
    nullDisplayStyle: overrideFormat ? NULL_DISPLAY_TO_BE[format.nullDisplay] : null,
    dateFormatPattern: overrideFormat && isDate ? format.dateFormatPattern : null,
    filterRules: toRuleSetType(filterRules),
    suppressionRules: toRuleSetType(suppressionRules),
  };
};

/** Leaf config → raw component settings. Components persist only field type
 * + rules; format columns stay at their BLANK (inherit) values. Node leaves
 * expose VALUE / QUANTITY in the Node types card; other leaves round-trip
 * their persisted field type. */
const toLeafRawSettings = (
  componentSettings: FormulaComponentSettings,
  fieldType: FieldDataType,
): RawSettings => ({
  ...BLANK_RAW_SETTINGS,
  fieldType,
  filterRules: toRuleSetType(componentSettings.filterRules),
  suppressionRules: toRuleSetType(componentSettings.suppressionRules),
});

/** Path-keyed form buffer → nested component tree on the raw fragment.
 * Path keys decompose into anchor chains: intermediate segments become
 * config-less anchor nodes carrying `children`; the final segment carries
 * the leaf config. Since the buffer holds exactly the current expansion's
 * leaves (seeded/pruned by the panel), stale paths — e.g. components
 * archived out of the formula — drop out here on save. */
const buildComponentTree = (
  formulaComponents: Record<string, FormulaComponentSettings>,
): QueryFieldComponentTreeNodeWrite[] => {
  const roots: QueryFieldComponentTreeNodeWrite[] = [];
  const byPath = new Map<string, QueryFieldComponentTreeNodeWrite>();

  for (const [pathKey, componentSettings] of Object.entries(formulaComponents)) {
    const segments = pathKey.split("/").map(Number);
    let siblings = roots;
    let prefix = "";
    segments.forEach((formulaComponentId, index) => {
      prefix = prefix ? `${prefix}/${formulaComponentId}` : String(formulaComponentId);
      let node = byPath.get(prefix);
      if (!node) {
        node = {
          __typename: "QueryFieldComponentType",
          id: 0,
          formulaComponentId,
          settings: null,
          datePeriod: null,
          levels: null,
          children: [],
        };
        byPath.set(prefix, node);
        siblings.push(node);
      }
      if (index === segments.length - 1) {
        const { fieldType } = componentSettings;
        node.settings =
          fieldType === null ? null : toLeafRawSettings(componentSettings, fieldType);
        node.datePeriod = applyDatePeriod(null, componentSettings.datePeriod);
        node.levels = { __typename: "LevelStackType", levels: componentSettings.levels };
      }
      siblings = node.children;
    });
  }
  return roots;
};

/** Next persisted level stack for the write path.
 *
 * A non-numeric primary field's Levels card only renders when the catalogue
 * lists the column's supported levels, so while that data is missing the
 * form carries an empty stack that would silently wipe a saved one. Such stacks are
 * therefore only cleared via the explicit "None" sentinel — an empty form
 * value preserves what's persisted. Stacks saved under a numeric type are
 * exempt: their card doesn't depend on the catalogue, and a numeric →
 * non-numeric type change intentionally clears the old rollup grains. */
const toNextLevelStack = (
  raw: QueryFieldFieldsFragment,
  values: FieldSettingsValues,
  shouldApplyLevels: boolean,
): string[] => {
  const next = shouldApplyLevels ? stripNoneLevel(values.levels) : [];
  if (next.length > 0) return next;
  const persisted = raw.levels?.levels ?? [];
  const preservePersisted =
    raw.referenceKind === "PRIMARY_FIELD" &&
    persisted.length > 0 &&
    !isNoneLevelSelection(values.levels) &&
    !isNumericFieldType(raw.settings?.fieldType ?? UNKNOWN_PRIMARY_FIELD_TYPE);
  return preservePersisted ? [...persisted] : next;
};

/** Form values → updated raw fragment. The top-level Save then runs the
 * existing `toQueryFieldInputs` write path against the mutated rows. */
export const applyFieldSettingsToRaw = (
  raw: QueryFieldFieldsFragment,
  values: FieldSettingsValues,
  attributeLeaves: ReadonlyArray<AttributeLeaf> = [],
): QueryFieldFieldsFragment => {
  const baseSettings = raw.settings ?? BLANK_RAW_SETTINGS;
  const updatedSettings: RawSettings | null = shouldPreserveUnknownPrimarySettings(
    raw,
    values,
    attributeLeaves,
  )
    ? null
    : {
        ...applyFormatToSettings(
          baseSettings,
          values.fieldType,
          values.format,
          values.overrideFormat,
          values.overrideDecimalPlaces,
          values.filterRules,
          values.suppressionRules,
        ),
        fieldType: values.fieldType,
      };

  const shouldApplyDatePeriod = isNumericFieldType(values.fieldType);
  const shouldApplyLevels = shouldShowLevels(
    raw.referenceKind,
    values.fieldType,
    toPrimaryFieldSupportedLevels(raw, attributeLeaves),
  );

  const hasFormulaComponents = Object.keys(values.formulaComponents).length > 0;
  const isNumericFormula =
    raw.referenceKind === "FORMULA" && isNumericFieldType(values.fieldType);
  // The buffer is authoritative once it carries leaves or the expansion
  // resolved (a resolved-empty buffer means the formula genuinely has no
  // leaf components, so persisted components must be cleared on save).
  const isComponentBufferAuthoritative =
    hasFormulaComponents || values.formulaComponentsResolved;

  let datePeriod: QueryFieldFieldsFragment["datePeriod"] = null;
  if (shouldApplyDatePeriod) {
    if (isNumericFormula && hasFormulaComponents) {
      datePeriod = null;
    } else if (isNumericFormula) {
      datePeriod = raw.datePeriod ?? null;
    } else {
      datePeriod = applyDatePeriod(raw.datePeriod, values.datePeriod);
    }
  }

  // `mergeField` is carried through untouched: the backend owns it and the
  // write path echoes it back to retain the field's tag.
  return {
    ...raw,
    settings: updatedSettings,
    datePeriod,
    // The "None" sentinel is a form-only value — an explicit no-level
    // choice persists as an empty stack (page-level execution).
    levels: {
      __typename: "LevelStackType" as const,
      levels: toNextLevelStack(raw, values, shouldApplyLevels),
    },
    // An empty, unresolved buffer means the expansion never loaded (or the
    // field is not a numeric formula) — preserve whatever the raw row
    // already carries rather than wiping persisted config.
    components:
      isNumericFormula && isComponentBufferAuthoritative
        ? buildComponentTree(values.formulaComponents)
        : raw.components,
  };
};
